import { getBrush } from "../brushes/brushRegistry";
import { resolveBrushStyle } from "../brushes/brushTypes";
import { interpolateStrokePoints } from "./canvasMath";
import type { DrawingSettings, StrokePoint } from "./canvasTypes";
import type { StrokeCommand } from "../history/historyTypes";
import type { Rect } from "../comic/comicTypes";

function deterministicNoise(index: number, salt: number): number {
  const value = Math.sin(index * 12.9898 + salt * 78.233) * 43758.5453;
  return (value - Math.floor(value)) * 2 - 1;
}

export class StrokeRenderer {
  private readonly context: CanvasRenderingContext2D;
  private settings: DrawingSettings | null = null;
  private lastPoint: StrokePoint | null = null;
  private segmentIndex = 0;
  private clipRect: Rect | null = null;

  public constructor(private readonly canvas: HTMLCanvasElement) {
    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error("Canvas 2D is not supported by this browser.");
    }
    this.context = context;
  }

  public getCanvas(): HTMLCanvasElement {
    return this.canvas;
  }

  public get activeTool(): DrawingSettings["tool"] | null {
    return this.settings?.tool ?? null;
  }

  public begin(settings: DrawingSettings, point: StrokePoint, clipRect?: Readonly<Rect>): void {
    this.clear();
    this.settings = { ...settings };
    this.lastPoint = point;
    this.segmentIndex = 0;
    this.clipRect = clipRect ? { ...clipRect } : null;
    this.drawDot(point);
  }

  public add(point: StrokePoint): void {
    if (!this.settings || !this.lastPoint) return;

    const brush = getBrush(this.settings.tool);
    const initialStyle = resolveBrushStyle(brush, this.settings, this.lastPoint.pressure);
    const minimumMovement = Math.max(0.35, initialStyle.width * 0.025);
    const distance = Math.hypot(point.x - this.lastPoint.x, point.y - this.lastPoint.y);
    if (distance < minimumMovement) return;

    // Short interpolated segments fill gaps from sparse pointer events without
    // delaying the line behind a long smoothing buffer.
    const spacing = Math.max(1, initialStyle.width * brush.interpolationSpacing);
    const points = interpolateStrokePoints(this.lastPoint, point, spacing);
    let from = this.lastPoint;
    for (const to of points) {
      this.drawSegment(from, to);
      from = to;
    }
    this.lastPoint = point;
  }

  public renderCommand(command: StrokeCommand): void {
    const first = command.points[0];
    if (!first) return;
    this.begin(command.settings, first, command.clipRect);
    for (const point of command.points.slice(1)) this.add(point);
  }

  public clear(): void {
    this.context.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.settings = null;
    this.lastPoint = null;
    this.segmentIndex = 0;
    this.clipRect = null;
  }

  private drawDot(point: StrokePoint): void {
    if (!this.settings) return;
    const style = resolveBrushStyle(getBrush(this.settings.tool), this.settings, point.pressure);
    const context = this.context;
    context.save();
    this.applyClip(context);
    context.globalCompositeOperation = "source-over";
    context.fillStyle = style.colour;
    context.globalAlpha = style.texture === "crayon" ? style.opacity * 0.8 : style.opacity;
    context.beginPath();
    context.arc(point.x, point.y, style.width / 2, 0, Math.PI * 2);
    context.fill();
    context.restore();
  }

  private drawSegment(from: StrokePoint, to: StrokePoint): void {
    if (!this.settings) return;
    const brush = getBrush(this.settings.tool);
    const style = resolveBrushStyle(brush, this.settings, to.pressure);

    if (style.texture === "crayon") {
      this.drawCrayonSegment(from, to, style.width, style.colour, style.opacity);
    } else if (style.texture === "marker") {
      this.drawMarkerSegment(from, to, style.width, style.colour, style.opacity);
    } else {
      this.drawLine(from, to, style.width, style.colour, style.opacity);
    }
    this.segmentIndex += 1;
  }

  private drawLine(
    from: StrokePoint,
    to: StrokePoint,
    width: number,
    colour: string,
    opacity: number,
  ): void {
    const context = this.context;
    context.save();
    this.applyClip(context);
    context.globalCompositeOperation = "source-over";
    context.strokeStyle = colour;
    context.globalAlpha = opacity;
    context.lineWidth = width;
    context.lineCap = "round";
    context.lineJoin = "round";
    context.beginPath();
    context.moveTo(from.x, from.y);
    context.lineTo(to.x, to.y);
    context.stroke();
    context.restore();
  }

  private drawMarkerSegment(
    from: StrokePoint,
    to: StrokePoint,
    width: number,
    colour: string,
    opacity: number,
  ): void {
    this.drawLine(from, to, width * 1.08, colour, opacity * 0.28);
    this.drawLine(from, to, width, colour, opacity * 0.72);
  }

  private drawCrayonSegment(
    from: StrokePoint,
    to: StrokePoint,
    width: number,
    colour: string,
    opacity: number,
  ): void {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const length = Math.max(1, Math.hypot(dx, dy));
    const normalX = -dy / length;
    const normalY = dx / length;

    this.drawLine(from, to, width * 0.72, colour, opacity * 0.62);
    for (let pass = 0; pass < 3; pass += 1) {
      const offset = deterministicNoise(this.segmentIndex, pass + 1) * width * 0.28;
      const jitter = deterministicNoise(this.segmentIndex + 7, pass + 4) * width * 0.05;
      this.drawLine(
        {
          ...from,
          x: from.x + normalX * offset,
          y: from.y + normalY * offset,
        },
        {
          ...to,
          x: to.x + normalX * (offset + jitter),
          y: to.y + normalY * (offset + jitter),
        },
        Math.max(0.75, width * 0.16),
        colour,
        opacity * (0.2 + pass * 0.04),
      );
    }
  }

  private applyClip(context: CanvasRenderingContext2D): void {
    if (!this.clipRect) return;
    context.beginPath();
    context.rect(this.clipRect.x, this.clipRect.y, this.clipRect.width, this.clipRect.height);
    context.clip();
  }
}
